import { keepPreviousData, useQuery } from "@tanstack/react-query";
import { indexationService, type IndexTargetsParams } from "@/services/IndexationService";

export const useIndexationSummary = (from?: string | null, to?: string | null) =>
  useQuery({
    queryKey: ["indexation", "summary", from ?? null, to ?? null] as const,
    queryFn: () => indexationService.getSummary(from, to),
    staleTime: 1000 * 60 * 5,
    throwOnError: false,
  });

// Checker health must age without a reload (checks_24h = 0 → "not running"), so
// it refetches on an interval — same reasoning as the Clicks freshness badges.
export const useIndexationCheckerHealth = () =>
  useQuery({
    queryKey: ["indexation", "checker-health"] as const,
    queryFn: () => indexationService.getCheckerHealth(),
    staleTime: 1000 * 60 * 2,
    refetchInterval: 1000 * 60 * 5,
    throwOnError: false,
  });

// Active VIP (pinned-override) target ids — a handful; used to mark the VIP
// column and to drive the VIP-only filter. Cached a bit longer than the grid.
export const useVipTargetIds = () =>
  useQuery({
    queryKey: ["indexation", "vip-ids"] as const,
    queryFn: () => indexationService.getVipTargetIds(),
    staleTime: 1000 * 60 * 5,
    throwOnError: false,
  });

export const useIndexationTargets = (params: IndexTargetsParams) =>
  useQuery({
    queryKey: ["indexation", "targets", params] as const,
    queryFn: () => indexationService.getTargets(params),
    placeholderData: keepPreviousData,
    staleTime: 1000 * 60 * 2,
    throwOnError: false,
  });

// A target's transition history — fetched only when its history drawer opens.
// No keepPreviousData: the drawer remounts per target, so a stale flash of
// another target's history would be wrong. Range bounds the events shown.
export const useTargetTimeline = (
  targetId: number | null,
  from: string | null,
  to: string | null,
  enabled: boolean,
) =>
  useQuery({
    queryKey: ["indexation", "timeline", targetId, from, to] as const,
    queryFn: () => indexationService.getTargetTimeline(targetId as number, from, to),
    enabled: enabled && Boolean(targetId),
    staleTime: 1000 * 60 * 2,
    throwOnError: false,
  });

// URL children of a domain — fetched only when the domain row is expanded, so
// opening N rows is N lazy queries, not N eager ones.
export const useIndexationUrlChildren = (domain: string | null, enabled: boolean) =>
  useQuery({
    queryKey: ["indexation", "url-children", domain] as const,
    queryFn: () => indexationService.getUrlChildren(domain as string),
    enabled: enabled && Boolean(domain),
    staleTime: 1000 * 60 * 2,
    throwOnError: false,
  });
